import {LiveAnnouncer} from '@angular/cdk/a11y';
import {COMMA, DOWN_ARROW, ENTER, MAC_ENTER, SEMICOLON, UP_ARROW} from '@angular/cdk/keycodes';
import {Component, effect, inject, signal} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatChipsModule} from '@angular/material/chips';
import {MatDialogModule} from '@angular/material/dialog';
import {MatError, MatFormFieldModule, MatLabel} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatListModule} from '@angular/material/list';
import {MatTableModule} from '@angular/material/table';
import {FieldTypeConfig, FormlyAttributes, FormlyValidationMessage} from '@ngx-formly/core';
import {FieldType} from '@ngx-formly/material';

@Component({
  standalone: true,
  selector: 'app-chip-list-form-field',
  templateUrl: './chip-list-form-field.component.html',
  styleUrl: './chip-list-form-field.component.scss',
  imports: [
    FormlyAttributes,
    MatButtonModule,
    MatChipsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatError,
    MatLabel,
    MatListModule,
    MatTableModule,
    ReactiveFormsModule,
    FormlyValidationMessage,
  ],
})
export class ChipListFormFieldComponent extends FieldType<FieldTypeConfig> {
  readonly items = signal([]);
  announcer = inject(LiveAnnouncer);

  /** Keyboard keys that can be used to separate chips when pressed. */
  separatorKeyCodes: number[] = [COMMA, ENTER, SEMICOLON, MAC_ENTER, DOWN_ARROW, UP_ARROW];
  separatorChars: string[] = [',', ';', '\t', '\n', '\r'];

  constructor() {
    super();
    effect(() => {
      const modelValue = this.model[this.field.key.toString()];
      if (Array.isArray(modelValue)) {
        this.items.set(modelValue);
      } else {
        this.items.set([]);
      }
    });
  }

  /**
   * Takes a chip list item and an optional validator name, and returns a boolean indicating whether it is invalid.
   * If a validator name is provided (via props.invalidItemValidator), only that validator's error array is checked.
   * Otherwise, all error arrays on the control are checked.
   *
   * @param item
   * @param validatorName
   */
  isInvalidItem(item: string, validatorName?: string): boolean {
    const errors = this.formControl.errors;
    if (!errors) return false;
    if (validatorName) {
      const validatorErrors = errors[validatorName];
      return Array.isArray(validatorErrors) && validatorErrors.includes(item);
    }
    return Object.values(errors).some(errorValue => Array.isArray(errorValue) && errorValue.includes(item));
  }

  removeItem(item: string) {
    this.items.update(items => {
      const index = items.indexOf(item);
      if (index < 0) {
        return items;
      }

      const next = [...items];
      next.splice(index, 1);
      this.formControl.setValue(next);

      this.announcer.announce(`removed ${item}`);
      return next;
    });
  }
  addItem(chipInput: HTMLInputElement): void {
    const trimmed = chipInput.value.trim();

    // Add the new item(s) and deduplicate the list
    if (trimmed) {
      // Split the input value on any of the separator characters and trim each part
      const pattern = new RegExp(`[${this.separatorChars.join('')}]`);
      const newItems = trimmed
        .split(pattern)
        .map(newItem => newItem.trim())
        .filter(newItem => newItem.length > 0);
      this.items.update(items => {
        const next = [...new Set([...items, ...newItems])];

        this.formControl.setValue(next);
        return next;
      });
    }

    // Clear the input value
    chipInput.value = '';
  }
}
